import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { ButtonUI, CustomThemeProvider, ModalUI, usePortal } from 'sonata-react-components';
import { getDoohReportOtaLevelThreshold } from 'sonata-static-storage/lib/helpers/doohReport.helpers';
import { useTheme } from 'styled-components';

import OtaBadge from '@/components/doohReport/common/OtaBadge/OtaBadge';

import './otaInfoModal.scss';

type ModalHandler = { closeModal: () => void; openModal: () => void };

type Props = {
  id: string;
  isOpen: boolean;
  onClose: () => void;
};

/** Illustrative figures of the modal's example, as in the design. */
const EXAMPLE = { baselinePct: 10, sonataPct: 20 } as const;
/** The design draws each example share 3.5 times wider than its value (10% → 35% of the track). */
const EXAMPLE_BAR_SCALE = 3.5;

/**
 * "What is OTA?" modal: explains the On-target Audience uplift with an illustrative
 * example, how it is calculated and how its rating bands read.
 */
export default function OtaInfoModal({ id, isOpen, onClose }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { themeContrast } = useTheme() as { themeContrast: 'light' | 'dark' | 'darkAI' };
  const modalRef = useRef<ModalHandler>(null);

  useEffect(() => {
    if (isOpen) {
      modalRef.current?.openModal();
    } else {
      modalRef.current?.closeModal();
    }
  }, [isOpen]);

  const exampleRows = [
    {
      key: 'baseline',
      label: formatMessage({ defaultMessage: 'Untargeted plan', id: 'doohReport.otaInfoModal.exampleBaseline' }),
      pct: EXAMPLE.baselinePct
    },
    {
      key: 'sonata',
      label: formatMessage({ defaultMessage: 'Planned with Sonata', id: 'doohReport.otaInfoModal.exampleSonata' }),
      pct: EXAMPLE.sonataPct
    }
  ];

  const modal = ReactDOM.createPortal(
    <CustomThemeProvider themeContrast={themeContrast}>
      <ModalUI
        className="otaInfoModal"
        closeModalCallback={onClose}
        id={id}
        ref={modalRef}
        title={formatMessage({ defaultMessage: 'What is OTA?', id: 'doohReport.otaInfoModal.title' })}
        width="770px"
      >
        <div className="otaInfoModal__content">
          <p className="otaInfoModal__intro">
            {formatMessage({
              defaultMessage:
                'OTA (On-target Audience uplift) answers one question: how much more of your intended audience did you reach for the same spend? It compares your plan with Sonata against an untargeted plan (random, across all available screens and hours) with the same budget, and is shown as an uplift (x2, x3).',
              id: 'doohReport.otaInfoModal.intro'
            })}
          </p>

          <div className="otaInfoModal__example">
            <span className="otaInfoModal__exampleTitle">
              {formatMessage({ defaultMessage: 'Example', id: 'doohReport.otaInfoModal.example' })}
            </span>
            {exampleRows.map(row => (
              <div className="otaInfoModal__exampleRow" key={row.key}>
                <span className="otaInfoModal__exampleLabel">{row.label}</span>
                <div className="otaInfoModal__exampleTrack">
                  <div
                    className={`otaInfoModal__exampleBar otaInfoModal__exampleBar--${row.key}`}
                    style={{ width: `${row.pct * EXAMPLE_BAR_SCALE}%` }}
                  />
                </div>
                <span className={`otaInfoModal__examplePct otaInfoModal__examplePct--${row.key}`}>{row.pct}%</span>
              </div>
            ))}
            <div className="otaInfoModal__exampleResult">
              <OtaBadge />
              <span className="otaInfoModal__exampleOta">x{EXAMPLE.sonataPct / EXAMPLE.baselinePct}</span>
              <span className="otaInfoModal__exampleExplanation">
                {formatMessage(
                  {
                    defaultMessage:
                      '{sonata}% ÷ {baseline}% = {ota} → twice as much of your target audience reached for the same spend',
                    id: 'doohReport.otaInfoModal.exampleExplanation'
                  },
                  {
                    baseline: EXAMPLE.baselinePct,
                    ota: EXAMPLE.sonataPct / EXAMPLE.baselinePct,
                    sonata: EXAMPLE.sonataPct
                  }
                )}
              </span>
            </div>
          </div>

          <div className="otaInfoModal__calculation">
            <h4 className="otaInfoModal__subtitle">
              {formatMessage({ defaultMessage: 'How it’s calculated', id: 'doohReport.otaInfoModal.howCalculated' })}
            </h4>
            <span className="otaInfoModal__formula">
              {formatMessage({
                defaultMessage:
                  'OTA = % on-target audience with Sonata ÷ % on-target audience of an untargeted plan at the same spend',
                id: 'doohReport.otaInfoModal.formula'
              })}
            </span>
            <p className="otaInfoModal__text">
              {formatMessage({
                defaultMessage:
                  'Sonata estimates how concentrated your audience is in each zone and hour, ranks those combinations, builds the plan with the most concentrated ones and compares it with a random plan at the same spend.',
                id: 'doohReport.otaInfoModal.method'
              })}
            </p>
            <p className="otaInfoModal__text">
              {formatMessage(
                {
                  defaultMessage:
                    'How good is my OTA? Below x{good} Low · from x{good} Good · from x{strong} Strong · x{excellent} or more Excellent. x1 means no uplift.',
                  id: 'doohReport.otaInfoModal.rating'
                },
                {
                  excellent: getDoohReportOtaLevelThreshold('excellent'),
                  good: getDoohReportOtaLevelThreshold('good'),
                  strong: getDoohReportOtaLevelThreshold('strong')
                }
              )}
            </p>
            <p className="otaInfoModal__text">
              {formatMessage({
                defaultMessage:
                  'What it means: OTA is not individual targeting. It measures how well the plan picks where and when your audience is, using aggregated observations of real people near the screens. With an OTA of x2, the same budget reaches twice as many people from your target, so each on-target impact costs half as much.',
                id: 'doohReport.otaInfoModal.meaning'
              })}
            </p>
          </div>

          <p className="otaInfoModal__note">
            {formatMessage({
              defaultMessage:
                'Illustrative example with round figures. The baseline is an untargeted plan at the same spend, not an average of other campaigns. OTA works especially well in DOOH (fixed screens, predictable movement, audiences that change by hour) and tends to be lower in very local campaigns or with few screens.',
              id: 'doohReport.otaInfoModal.note'
            })}
          </p>

          <div className="otaInfoModal__footer">
            <ButtonUI
              id={`${id}-closeButton`}
              onClick={onClose}
              text={formatMessage({ defaultMessage: 'Close', id: 'doohReport.otaInfoModal.close' })}
              type="secondary"
            />
          </div>
        </div>
      </ModalUI>
    </CustomThemeProvider>,
    usePortal(id)
  );

  return isOpen ? modal : null;
}
